import { useMemo, useState, useEffect } from "react";
import { Mail, CheckCircle2, XCircle, X, Activity, Clock, RefreshCw } from "lucide-react";

const KIND_LABELS = {
  booking_confirmation: "Confirmation réservation",
  loueur_new_booking: "Alerte loueur",
  cancellation: "Annulation",
  activity_confirmation: "Confirmation activité",
  review_invitation: "Invitation avis",
  activity_review_invitation: "Invitation avis activité",
  deposit_released: "Caution libérée",
  dispute_alert: "Contestation caution",
  password_reset: "Réinitialisation mot de passe",
  "2fa_code": "Code de connexion (2FA)",
  client_tips: "Conseils location",
};

const SELECT_CLS =
  "h-9 rounded-lg border border-border bg-white px-3 text-sm font-body text-ink focus:outline-none focus:ring-2 focus:ring-canard/40";

export function EmailLogsPanel({ logs = [], queue = [], initialEmail = "", onRetry }) {
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addr, setAddr] = useState(initialEmail);

  useEffect(() => setAddr(initialEmail), [initialEmail]);

  const kinds = useMemo(() => Array.from(new Set(logs.map((l) => l.kind).filter(Boolean))), [logs]);

  const health = useMemo(() => {
    const weekAgo = Date.now() - 7 * 864e5;
    const recent = logs.filter((l) => l.created_at && new Date(l.created_at).getTime() >= weekAgo);
    const sent = recent.filter((l) => l.status === "sent").length;
    const rate = recent.length ? Math.round((sent / recent.length) * 100) : null;
    return { total: recent.length, sent, failed: recent.length - sent, rate };
  }, [logs]);

  const retryingIds = useMemo(() => new Set(queue.map((q) => q.log_id).filter(Boolean)), [queue]);
  const hasData = health.total > 0;
  const healthColor = !hasData ? "text-muted2" : health.rate >= 95 ? "text-emeraude-dark" : health.rate >= 80 ? "text-amber-600" : "text-red-600";
  const healthBg = !hasData ? "bg-sand" : health.rate >= 95 ? "bg-emeraude/10" : health.rate >= 80 ? "bg-amber-100" : "bg-red-100";

  const filtered = useMemo(
    () =>
      logs.filter(
        (l) =>
          (kindFilter === "all" || l.kind === kindFilter) &&
          (statusFilter === "all" || l.status === statusFilter) &&
          (!addr || (l.to || "").toLowerCase().includes(addr.toLowerCase()))
      ),
    [logs, kindFilter, statusFilter, addr]
  );

  return (
    <div data-testid="admin-email-logs">
      <div className={`flex items-center gap-4 mb-5 p-4 rounded-2xl ${healthBg}`} data-testid="email-health">
        <div className={`w-11 h-11 rounded-full bg-white/70 flex items-center justify-center ${healthColor}`}>
          <Activity className="w-5 h-5" />
        </div>
        <div>
          <p className={`text-2xl font-heading font-extrabold ${healthColor}`} data-testid="email-health-rate">
            {hasData ? `${health.rate}% envoyés` : "— aucun envoi récent"}
          </p>
          <p className="text-xs text-muted2 font-body">
            7 derniers jours · {health.sent}/{health.total} réussis{health.failed > 0 ? ` · ${health.failed} échec(s)` : ""}
          </p>
        </div>
      </div>

      {queue.length > 0 && (
        <div data-testid="email-queue" className="mb-5 bg-white rounded-2xl shadow-card border border-amber-200 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-200">
            <Clock className="w-4 h-4 text-amber-600" />
            <p className="font-heading font-bold text-ink text-sm">File d'attente de relance ({queue.length})</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand text-left text-xs uppercase text-muted2 font-semibold">
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Destinataire</th>
                  <th className="px-4 py-2">Tentatives</th>
                  <th className="px-4 py-2">Prochain essai</th>
                  <th className="px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((q) => (
                  <tr key={q.id} data-testid={`email-queue-${q.id}`} className="border-t border-border/60">
                    <td className="px-4 py-2 font-semibold text-ink">{KIND_LABELS[q.kind] || q.kind}</td>
                    <td className="px-4 py-2 text-muted2 truncate max-w-[200px]" title={q.to}>{q.to}</td>
                    <td className="px-4 py-2 text-muted2">{q.attempts}/3</td>
                    <td className="px-4 py-2 text-muted2 whitespace-nowrap">{q.next_retry_at ? new Date(q.next_retry_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2">
                      <button data-testid={`email-retry-${q.id}`} onClick={() => onRetry && onRetry(q.id)}
                        className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-canard/10 text-canard hover:bg-canard/20 transition-colors">
                        <RefreshCw className="w-3.5 h-3.5" /> Réessayer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select data-testid="email-filter-kind" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className={SELECT_CLS}>
          <option value="all">Tous les types</option>
          {kinds.map((k) => (
            <option key={k} value={k}>{KIND_LABELS[k] || k}</option>
          ))}
        </select>
        <select data-testid="email-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={SELECT_CLS}>
          <option value="all">Tous les statuts</option>
          <option value="sent">Envoyé</option>
          <option value="failed">Échec</option>
        </select>
        {addr && (
          <span data-testid="email-filter-addr" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-canard/10 text-canard text-sm font-semibold">
            {addr}
            <button data-testid="email-filter-addr-clear" onClick={() => setAddr("")} className="hover:text-canard-dark">
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        )}
        <span className="text-xs text-muted2 font-body" data-testid="email-filter-count">
          {filtered.length} / {logs.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border text-muted2 font-body">
          Aucun email pour ces critères.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand text-left text-xs uppercase text-muted2 font-semibold">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Destinataire</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`email-log-${l.id}`} className="border-t border-border/60">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
                        <Mail className="w-3.5 h-3.5 text-canard shrink-0" />
                        {KIND_LABELS[l.kind] || l.kind || l.subject}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted2 truncate max-w-[220px]" title={l.to}>{l.to}</td>
                    <td className="px-4 py-3">
                      {l.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-emeraude/15 text-emeraude-dark">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Envoyé
                        </span>
                      ) : retryingIds.has(l.id) ? (
                        <span title={l.error || ""} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          <Clock className="w-3.5 h-3.5" /> En relance
                        </span>
                      ) : (
                        <span title={l.error || ""} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                          <XCircle className="w-3.5 h-3.5" /> Échec
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted2 whitespace-nowrap">
                      {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
